import { createColumnHelper } from '@tanstack/react-table';
import { i18n } from 'i18n';
import { renderClassificationCell } from '../../../../common/components/ThreatListTable/cells/ClassificationCell';
import {
  renderThreatIdCell,
  renderThreatIdHeader,
} from '../../../../common/components/ThreatListTable/cells/ThreatIdCell';
import type { ThreatGroupUnion } from '../../../../threat.types';
import { renderActionsCell } from '../cells/ActionsCell';
import {
  renderAssociatedItemCell,
  renderAssociatedItemHeader,
} from '../cells/AssociatedItemCell';
import {
  renderDetectionDateCell,
  renderDetectionDateHeader,
} from '../cells/DetectionDateCell';
import { renderDevicesCell, renderDevicesHeader } from '../cells/DevicesCell';
import { renderMitreCell } from '../cells/MitreCell';
import { renderStatusCell, renderStatusHeader } from '../cells/StatusCell';

export type GetColumnsParams = {
  isFileDetectionType: boolean;
  query: string | undefined;
};

const getColumns = ({ isFileDetectionType, query }: GetColumnsParams) => {
  const columnHelper = createColumnHelper<ThreatGroupUnion>();

  const columns = [
    columnHelper.accessor('threat_id', {
      id: 'threat_id',
      header: (info) => renderThreatIdHeader(info, isFileDetectionType),
      cell: (info) => renderThreatIdCell(info, isFileDetectionType, query),
      minSize: isFileDetectionType ? 290 : 100,
      maxSize: isFileDetectionType ? 320 : 120,
      meta: {
        displayName: i18n.t('Threat ID'),
      },
    }),
    columnHelper.accessor('associated_item', {
      id: 'associated_item',
      header: (info) => renderAssociatedItemHeader(info, isFileDetectionType),
      cell: (info) =>
        renderAssociatedItemCell(info, isFileDetectionType, query),
      meta: {
        displayName: i18n.t('Associated Item'),
      },
      maxSize: 100,
    }),
    columnHelper.accessor('classification', {
      id: 'classification',
      header: i18n.t('Classification'),
      cell: renderClassificationCell,
      meta: {
        displayName: i18n.t('Classification'),
      },
      maxSize: 80,
    }),
    columnHelper.accessor('last_detection_date', {
      id: 'detection_date',
      header: renderDetectionDateHeader,
      cell: renderDetectionDateCell,
      meta: {
        hideCellTitle: true,
        displayName: i18n.t('Detection Date'),
      },
      maxSize: 90,
    }),
    columnHelper.accessor('device_count', {
      id: 'device_count',
      header: renderDevicesHeader,
      cell: renderDevicesCell,
      meta: {
        displayName: i18n.t('Devices'),
      },
      maxSize: 70,
    }),
    ...(!isFileDetectionType
      ? [
          columnHelper.accessor('mitre_techniques', {
            id: 'mitre',
            header: i18n.t('MITRE'),
            cell: renderMitreCell,
            enableSorting: false,
            meta: {
              hideCellTitle: true,
              displayName: i18n.t('MITRE'),
            },
            maxSize: 60,
          }),
        ]
      : []),
    columnHelper.display({
      id: 'status',
      header: (info) => renderStatusHeader(info, isFileDetectionType),
      cell: (info) => renderStatusCell(info, isFileDetectionType),
      meta: {
        hideCellTitle: true,
        displayName: i18n.t('Threat Status'),
      },
      size: isFileDetectionType ? 200 : 120,
    }),
    ...(isFileDetectionType
      ? [
          columnHelper.display({
            id: 'actions',
            cell: renderActionsCell,
            enableSorting: false,
            maxSize: 60,
          }),
        ]
      : []),
  ];

  return columns;
};

export default getColumns;
